import { useEffect, useState, type JSX } from 'react';
import { z } from 'zod';
import {
  handoffAlertSchema,
  handoffPolicySchema,
  type BotScope,
} from '@cryptology-assist/domain';
import { botPath, request, savedSchema, type Session } from './api';
import { ErrorNotice, useMutation } from './components';
import { useRemote } from './hooks';
import { dateLabel, type Copy, type Language } from './copy';

const policyResponse = z.object({ policy: handoffPolicySchema.nullable() });
const alertResponse = z.object({ alerts: z.array(handoffAlertSchema) });
const memberResponse = z.object({
  members: z.array(
    z.object({ account_id: z.string(), display_name: z.string() })
  ),
});

export function HandoffNotice({
  scope,
  copy,
  visible,
  onOpen,
}: {
  scope: BotScope;
  copy: Copy;
  visible: boolean;
  onOpen: () => void;
}): JSX.Element | null {
  const alerts = useRemote(
    visible ? botPath(scope, 'handoff-alerts') : null,
    alertResponse
  );
  useEffect(() => {
    if (!visible) return;
    const interval = window.setInterval(alerts.reload, 30_000);
    return () => window.clearInterval(interval);
  }, [visible, alerts.reload]);
  if (!visible) return null;
  if (alerts.error)
    return (
      <ErrorNotice error={alerts.error} copy={copy} onRetry={alerts.reload} />
    );
  if (!alerts.data?.alerts.length) return null;
  return (
    <div className="notice" role="status">
      <span>{copy.overdueHandoffs}</span>
      <button onClick={onOpen}>{copy.openHandoffAlerts}</button>
    </div>
  );
}

export function Handoff({
  scope,
  session,
  copy,
  language,
  canAdmin,
  canWrite,
}: {
  scope: BotScope;
  session: Session;
  copy: Copy;
  language: Language;
  canAdmin: boolean;
  canWrite: boolean;
}): JSX.Element {
  const policy = useRemote(botPath(scope, 'handoff-policy'), policyResponse);
  const alerts = useRemote(botPath(scope, 'handoff-alerts'), alertResponse);
  const members = useRemote(
    canAdmin ? botPath(scope, 'conversations/assignees') : null,
    memberResponse
  );
  const mutation = useMutation();
  const [initial, setInitial] = useState('');
  const [escalation, setEscalation] = useState('');
  const [minutes, setMinutes] = useState('');
  useEffect(() => {
    if (policy.data?.policy) {
      setInitial(policy.data.policy.initialAssigneeId);
      setEscalation(policy.data.policy.escalationAssigneeId);
      setMinutes(String(policy.data.policy.responseMinutes));
    }
  }, [policy.data]);
  useEffect(() => {
    const interval = window.setInterval(alerts.reload, 30_000);
    return () => window.clearInterval(interval);
  }, [alerts.reload]);
  return (
    <section className="settings-page">
      <h1>{copy.handoffOperations}</h1>
      <ErrorNotice
        error={policy.error ?? alerts.error ?? members.error ?? mutation.error}
        copy={copy}
        onRetry={() => {
          policy.reload();
          alerts.reload();
          members.reload();
        }}
      />
      {policy.loading && <p>{copy.loading}</p>}
      {policy.data && !policy.data.policy && (
        <p role="status">{copy.handoffPolicyMissing}</p>
      )}
      {canAdmin && members.data && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void mutation.run(async () => {
              const body = handoffPolicySchema.parse({
                initialAssigneeId: initial,
                escalationAssigneeId: escalation,
                responseMinutes: Number(minutes),
              });
              await request(botPath(scope, 'handoff-policy'), savedSchema, {
                body,
                csrf: session.csrf,
              });
              policy.reload();
            });
          }}
        >
          <label>
            {copy.initialAssignee}
            <select
              required
              value={initial}
              onChange={(event) => setInitial(event.target.value)}
            >
              <option value="">{copy.selectAssignee}</option>
              {members.data.members.map((member) => (
                <option key={member.account_id} value={member.account_id}>
                  {member.display_name}
                </option>
              ))}
            </select>
          </label>
          <label>
            {copy.escalationAssignee}
            <select
              required
              value={escalation}
              onChange={(event) => setEscalation(event.target.value)}
            >
              <option value="">{copy.selectAssignee}</option>
              {members.data.members.map((member) => (
                <option key={member.account_id} value={member.account_id}>
                  {member.display_name}
                </option>
              ))}
            </select>
          </label>
          <label>
            {copy.responseMinutes}
            <input
              required
              type="number"
              min="1"
              max="10080"
              value={minutes}
              onChange={(event) => setMinutes(event.target.value)}
            />
          </label>
          <button type="submit" disabled={mutation.busy}>
            {copy.saveHandoffPolicy}
          </button>
        </form>
      )}
      <h2>{copy.overdueHandoffs}</h2>
      {alerts.loading && !alerts.data && <p>{copy.loading}</p>}
      {alerts.data?.alerts.length === 0 && <p>{copy.noHandoffAlerts}</p>}
      {alerts.data?.alerts.map((alert) => (
        <article key={alert.id}>
          <a
            href={`/?botId=${encodeURIComponent(scope.botId)}&conversationId=${encodeURIComponent(alert.conversationId)}`}
          >
            {copy.openConversation}
          </a>
          <p>{dateLabel(alert.dueAt, language)}</p>
          {canWrite && (
            <button
              disabled={mutation.busy}
              onClick={() =>
                void mutation.run(async () => {
                  await request(
                    botPath(scope, `handoff-alerts/${alert.id}`),
                    savedSchema,
                    { body: {}, csrf: session.csrf }
                  );
                  alerts.reload();
                })
              }
            >
              {copy.acknowledgeAlert}
            </button>
          )}
        </article>
      ))}
    </section>
  );
}
